/**
 * Shopify paginates its native product drops, not an arbitrarily filtered array.
 * Assemble the native, market-localized pages first, then paginate visible cards.
 * No product JSON, prices, publication, inventory or search query is rewritten.
 * Without JS / on failure the native pages remain navigable, with a per-page count.
 */
class SCDiscoveryGrid extends HTMLElement {
  static observedAttributes = ['data-sort', 'data-page', 'data-pages', 'data-query'];
  attributeChangedCallback() {
    if (this.isConnected) {
      clearTimeout(this.timer);
      this.connectedCallback();
    }
  }
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.timer = setTimeout(() => this.load(), 0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  async load() {
    const root = this.closest('[data-sc-discovery-results]');
    if (!root || this.dataset.empty === 'true') return;
    const status = this.querySelector('[data-sc-discovery-status]');
    const fr = this.dataset.fr === 'true';
    const signal = this.controller.signal;
    const sourceURL = new URL(location.href);
    const requestedPage = Math.max(1, Number(sourceURL.searchParams.get('page')) || 1);
    const pages = Number(this.dataset.pages);
    const current = Number(this.dataset.page);
    const size = Number(this.dataset.size);
    const grid = root.querySelector('.product-grid');
    if (!grid || !Number.isInteger(pages) || pages < 1 || !size) return;
    const cardsFrom = node => [...node.querySelectorAll('.product-grid > li[data-product-id]')];
    const batches = new Map([[current, cardsFrom(root)]]);
    try {
      // Bounded concurrency; fetch every native page (including pair-only pages).
      const queue = Array.from({length:pages}, (_, i) => i + 1).filter(p => p !== current);
      await Promise.all(Array.from({length:Math.min(3,queue.length)}, async () => {
        while (queue.length) {
          const page = queue.shift();
          const url = new URL(sourceURL);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', this.dataset.section);
          const response = await fetch(url, {signal, credentials:'same-origin'});
          if (!response.ok) throw new Error(`Discovery page HTTP ${response.status}`);
          const html = await response.text();
          if (/Liquid (?:error|syntax error)/i.test(html)) throw new Error('Discovery Liquid error');
          const doc = new DOMParser().parseFromString(html,'text/html');
          const metadata = doc.querySelector('sc-discovery-grid');
          if (!metadata || Number(metadata.dataset.page) !== page || Number(metadata.dataset.pages) !== pages) {
            throw new Error('Discovery pagination changed; reload required');
          }
          batches.set(page, cardsFrom(doc));
        }
      }));
      if (signal.aborted || !this.isConnected || location.href !== sourceURL.href) return;
      const seen = new Set();
      let cards = [...batches.entries()].sort((a,b)=>a[0]-b[0]).flatMap(([,cards])=>cards).filter(card=>{
        const id=card.dataset.productId;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      // Only promote exact titles for relevance ordering; retain every broad match.
      let exactCount=0;
      const query=this.dataset.query.trim().toLocaleLowerCase();
      if (this.dataset.kind === 'search' && (!sourceURL.searchParams.has('sort_by') || sourceURL.searchParams.get('sort_by') === 'relevance')) {
        cards=cards.map((card,index)=>({card,index,exact:card.dataset.scTitle?.trim().toLocaleLowerCase()===query}))
          .sort((a,b)=>Number(b.exact)-Number(a.exact)||a.index-b.index).map(x=>x.card);
        exactCount=cards.filter(card=>card.dataset.scTitle?.trim().toLocaleLowerCase()===query).length;
      }
      const totalPages=Math.max(1,Math.ceil(cards.length/size));
      const page=Math.min(requestedPage,totalPages);
      root.querySelectorAll('.pagination').forEach(node=>node.remove());
      const visibleCards=cards.slice((page-1)*size,page*size);
      const children=[];
      let previousGroup;
      for (const card of visibleCards) {
        if(exactCount>0 && exactCount<cards.length) {
          const group=card.dataset.scTitle?.trim().toLocaleLowerCase()===query?'exact':'related';
          if(group!==previousGroup) {
            const item=document.createElement('li');
            item.setAttribute('role','presentation');
            item.style.gridColumn='1 / -1';
            const heading=document.createElement('h2');
            heading.className='h4';
            heading.textContent=group==='exact'?(fr?'Correspondance exacte':'Exact match'):(fr?'Suggestions associées':'Related suggestions');
            item.append(heading);
            children.push(item);
            previousGroup=group;
          }
        }
        children.push(card);
      }
      grid.replaceChildren(...children);
      grid.dataset.lastPage=String(totalPages);
      status.textContent=fr ? `${cards.length} produits · Page ${page} / ${totalPages}` : `${cards.length} products · Page ${page} / ${totalPages}`;
      this.dataset.complete='true';
      if (totalPages>1) {
        const nav=document.createElement('nav');
        nav.setAttribute('aria-label',fr?'Pagination des produits':'Product pagination');
        for(let i=1;i<=totalPages;i++) {
          const link=document.createElement('a');
          const url=new URL(sourceURL);
          url.searchParams.set('page',String(i));
          url.searchParams.delete('section_id');
          link.href=url.href;
          link.textContent=String(i);
          link.setAttribute('aria-label',`Page ${i}`);
          if(i===page)link.setAttribute('aria-current','page');
          nav.append(link);
        }
        this.querySelector('nav')?.remove();
        this.append(nav);
      }
    } catch(error) {
      if(signal.aborted) return;
      status.textContent += fr ? ' — Affichage partiel : rechargez la page pour compter tous les produits.' : ' — Partial display: reload to count all products.';
      console.error('Discovery pagination:', error);
    }
  }
}
if (!customElements.get('sc-discovery-grid')) customElements.define('sc-discovery-grid',SCDiscoveryGrid);